import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../providers/AuthContext";
import GameService from "../../../service/gameService";
import { ReactComponent as IconPlayGame } from "../../../assets/icons/icon-play.svg";
import debounce from "lodash.debounce"; // Import debounce function
import { SectionLoader } from "../../../components/ui/loaders/SectionLoader";
import { CONFIG } from "../../../helpers/constants";
import Modal from "../../../components/ui/Modal";
import InfiniteScroll from "react-infinite-scroller";
import { SkeletonLoader } from "../../../components/ui/loaders/SkeletonLoader";

export const SearchGames = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const gameService = new GameService(CONFIG.BASE_URL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [games, setGames] = useState([]);
  const [totalGames, setTotalGames] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const { user } = useAuth();

  const isLoadingRef = useRef(false);
  let limit = 20;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const getAllGameByNames = async () => {
    // if (loading || searchTerm || !hasMore) return;
    isLoadingRef.current = true;
    try {
      setLoading(true);
      const response = await gameService.fetchAllGamesByName(offset + 1, offset <= 0 ? 16 : 8, searchTerm); // Use offset
      if (response.data.length && offset === 0) {
        setGames(response.data);
        setTotalGames(limit || 0);
      } else if (response.data.length) {
        setGames((prevGames) => {
          const newGames = response.data.filter((newGame) => !prevGames.some((prevGame) => prevGame._id === newGame._id));
          setTotalGames(prevGames.length || 0);
          return [...prevGames, ...newGames];
        });
      } else {
        setHasMore(false);
      }

      setTotalPages(response.totalPages || 0);
    } catch (error) {
      console.error("Failed to load games:", error);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };
  const handleSearchTerm = (e) => {
    setOffset(0);
    let value = e.target.value;
    setSearchTerm(value);
  };

  useEffect(() => {
    setLoading(true);
    const fetchGamesDebounced = debounce(() => {
      getAllGameByNames();
    }, 400); // ✅ Wait 300ms before triggering API request

    fetchGamesDebounced(searchTerm);

    return () => fetchGamesDebounced.cancel();
  }, [searchTerm]);

  const handleLoadMore = useCallback(() => {
    if (!isLoadingRef.current && hasMore) {
      isLoadingRef.current = true;
      setOffset((prevOffset) => prevOffset + 1);
    }
  }, [hasMore]);

  useEffect(() => {
    if (offset === 0) return; // Prevent unnecessary fetch on mount
    getAllGameByNames();
  }, [offset]);

  const gameLauncher = (gameId, provider) => {
    onClose();
    navigate(`/game-play/${gameId}${provider ? "?provider=" + provider : ""}`);
    const payload = {
      gameId: gameId,
      provider: provider,
    };
  };

  // Filter and sort games
  const filteredGames =
    searchTerm.length > 3 &&
    games.filter(
      (game) => game.name?.toLowerCase().includes(searchTerm.toLowerCase()) || game.provider_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  const displayedGames = searchTerm.length > 3 ? filteredGames?.slice(0, totalGames) : games;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="infinity-wrapper w-full lg:max-w-[60rem] lg:h-[calc(100vh-80px)] h-screen overflow-y-auto "
        title={<h2 className="mt-[20px] font-bold md:text-xl text-lg">Search Engine</h2>}
      >
        <div className="px-5 pb-5 overflow-y-auto">
          <div className="md:mt-4 bg-[#2e2e2e]">
            <div className="relative">
              <input
                autoFocus
                value={searchTerm}
                onChange={handleSearchTerm}
                placeholder="Type to Search (Game | Provider)"
                className="text-left bg-white text-gray-800 pr-8 pl-3 rounded-[8px] bg-no-repeat appearance-none outline-none focus:outline-none border-transparent w-full text-gray font-semibold px-3 py-2.5 mb-2.5 md:mb-4 mt-7 md:mt-0 text-sm md:text-xl placeholder:text-[#999999] placeholder:font-semibold focus:ring-primary-500"
              />
              <span className="absolute right-3 md:bottom-6 bottom-[14px] transform -translate-y-1/2 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="#000" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
            <p>Search requires at least 3 characters</p>
          </div>
          <h3 className="text-primary font-semibold mt-5 lg:text-xl text-lg">Latest Games</h3>
          {loading && offset === 0 ? (
            <SectionLoader />
          ) : displayedGames && displayedGames.length ? (
            <InfiniteScroll
              pageStart={0}
              loadMore={handleLoadMore}
              hasMore={hasMore}
              useWindow={false}
              getScrollParent={() => document.querySelector(".infinity-wrapper")}
              loader={
                <div className="grid lg:grid-cols-4 grid-cols-3 gap-4 pt-5">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <SkeletonLoader
                      key={index}
                      style={{
                        aspectRatio: "1",
                      }}
                    />
                  ))}
                </div>
              }
            >
              <div className={`${"grid lg:grid-cols-4 grid-cols-3 gap-4 pt-5"}`}>
                {displayedGames?.map((game) => (
                  <div
                    key={game.gameId}
                    className="relative bg-[#242424] overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transform transition-all"
                    style={{
                      aspectRatio: "1",
                    }}
                  >
                    <img src={game.image || "default-image-url.png"} alt={game.name} className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => (user ? gameLauncher(game.gameId || game.gameid, game.provider_name) : toast.error("Login to play game"))}
                        className=" px-4 py-2 rounded-full text-gray-900 font-bold shadow-lg transition [&_svg_path]:fill-primary"
                      >
                        <IconPlayGame />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </InfiniteScroll>
          ) : (
            <div className="flex items-center justify-center w-full py-10 bg-[#2E2E2E] text-white">No games available at the moment.</div>
          )}
        </div>
      </Modal>
    </>
  );
};
